import React from 'react';

import Select from 'react-select';

import {
  Badge,
  Button,
  Checkbox,
  Table,
  TablePropertyFiltering,
  TableSorting,
  TablePreferences,
  TableContentSelector,
  TablePagination,
} from 'AWS-UI-Components-React';

import { ProductGroupSelection } from './product_group_selection';
import { ReviewChanges } from './review_changes';
import { downloadFile, toDownloadFormat } from '../../../utilities';
import { isProductLineModified, accumulateProductLineById, isUnmappedGl } from '../../../utils/product_line';
import { PRODUCT_LINE_LATEST_VERSION, PRODUCT_LINE_ROOT, PRODUCT_LINE_STATUS } from '../../../constants/product_line';
import { buildMappedGlData, buildProductGroupOptions } from './option_builder';
import { V2_PROPERTY_FILTERING_TEXT, capFilterValues, v2PropertyFilteringFunction, getFilteredItems } from '../../common/property_filter_helpers';

const tablePageSize = 50;

const hiddenColumns = ['user', 'time', 'staged'];

const hiddenProductLineIds = ['-1'];

// Each array's elements must equal the filter option values offered for its property.
const ARRAY_FIELD_BY_PROPERTY_KEY = { _productGroupsText: '_productGroupTokens' };
const filterProductLine = (item, tokens, operation) =>
  v2PropertyFilteringFunction(item, tokens, operation, ARRAY_FIELD_BY_PROPERTY_KEY);

export class PLGLMappingTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      reviewChangesScreen: { visible: false },
      modifiedProductLinesById: {},
      filteringTokens: [],
      filteringOperation: 'and',
    };
  }

  getColumnDefinitions = (productGroupOptions, parentProductLineOptions, mappedGlIds, mappedGlOwnerById) => {
    return [
      {
        id: 'id',
        header: 'Id',
        cell: (item) => {
          return (
            <span>
              {item.id} {this.getBadges(item)}
            </span>
          );
        },
        minWidth: '100px',
      },
      {
        id: 'name',
        header: 'Name',
        cell: (item) => item.name,
        minWidth: '200px',
      },
      {
        id: 'gls',
        header: 'Product Groups',
        cell: (item) => this.productGroupsDisplay(item, productGroupOptions, mappedGlIds, mappedGlOwnerById),
        minWidth: '250px',
        allowLineWrap: true,
      },
      {
        id: 'parent',
        header: 'Parent',
        cell: (item) => this.parentSelectionComponent(item, parentProductLineOptions),
        minWidth: '200px',
      },
      {
        id: 'ancestorProductLine',
        header: 'Product Line Group',
        cell: (item) =>
          `${item.ancestorProductLineName ? item.ancestorProductLineName + ' (' + item.ancestorProductLine + ')' : ''}`,
        minWidth: '150px',
      },
      {
        id: 'excluded',
        header: 'Excluded',
        cell: (item) => (
          <Checkbox
            checked={item.excluded || false}
            controlId={item.id}
            onChange={(event) => this.onExcludedChange(event, item)}
            disabled={this.props.readOnly}
          />
        ),
      },
      {
        id: 'ignored',
        header: 'Ignored',
        cell: (item) => (
          <Checkbox
            checked={item.ignored || false}
            controlId={item.id}
            onChange={(event) => this.onIgnoredChange(event, item)}
            disabled={this.props.readOnly}
          />
        ),
      },
      {
        id: 'staged',
        header: 'Staged',
        cell: (item) => (item.isStaged ? 'Yes' : 'No'),
      },
      {
        id: 'user',
        header: 'Modified By User',
        cell: (item) => item.user,
      },
      {
        id: 'time',
        header: 'Modified Time',
        cell: (item) => (item.time ? new Date(item.time).toLocaleString() : '-'),
      },
      {
        id: 'comment',
        header: 'Comment',
        cell: (item) => item.comment,
      },
    ];
  };

  getBadges = (productLine) => {
    const gls = productLine.gls;
    const ignored = productLine.ignored;

    let badges = [];

    if (isUnmappedGl(gls,ignored)) {
      badges.push(
        <Badge key={`${PRODUCT_LINE_STATUS.UNMAPPED}_${productLine.id}`} color={'grey'}>
          {PRODUCT_LINE_STATUS.UNMAPPED}
        </Badge>
      );
    }

    if (productLine.isStaged) {
      badges.push(
        <Badge key={`${PRODUCT_LINE_STATUS.STAGED}_${productLine.id}`} color={'blue'}>
          {PRODUCT_LINE_STATUS.STAGED}
        </Badge>
      );
    }

    return badges.map((badge) => <span key={badge.key}>{badge} </span>);
  };

  productGroupsDisplay = (productLine, productGroupOptions, mappedGlIds, mappedGlOwnerById) => {
    const glIds = isUnmappedGl(productLine.gls) ? [] : productLine.gls;
    const selectedOptions = productGroupOptions.filter((option) => glIds.includes(parseInt(option.value)));

    // A GL mapped to a DIFFERENT product line is shown DISABLED (badged with the
    // owner id by the dropdown), not hidden. The current row's own GLs are exempt:
    // without that, the active selection above could not resolve and would vanish.
    const availableProductGroupOptions = buildProductGroupOptions(
      productGroupOptions,
      glIds,
      mappedGlIds,
      mappedGlOwnerById
    );

    return (
      <div key={`PRODUCT_GROUP_DISPLAY_${productLine.id}_${glIds.join()}`}>
        <ProductGroupSelection
          productGroupOptions={availableProductGroupOptions}
          selectedProductGroups={selectedOptions}
          onProductGroupSelection={(selectedOption) => this.onProductGroupSelection(productLine, selectedOption)}
          disabled={this.props.readOnly}
        />
      </div>
    );
  };

  /**
   * Build the set of GL product-group ids that are already mapped to some leaf
   * product line. Sentinel values (-1 / null / empty) are NOT mappings and are
   * skipped. Only leaf product lines hold `gls`, so non-leaf lines are ignored.
   *
   * Staged/unsaved edits are respected: the in-session `modifiedProductLinesById`
   * overlay takes precedence over the persisted `productLinesById`, so selecting
   * a GL in one row immediately marks it mapped for every other row (and
   * deselecting returns it).
   *
   * Memoized on the (productLinesById, modifiedProductLinesById) references so
   * the O(leafLines * gls) scan runs once per data change rather than once per
   * row render - the table can have many rows.
   */
  mappedGlProductGroupIds = (productLinesById, modifiedProductLinesById) => {
    return this.mappedGlData(productLinesById, modifiedProductLinesById).mappedGlIds;
  };

  /**
   * Owning-PL lookup (GL id -> { id, name }) for every mapped GL, used to badge a
   * disabled option with the owner id. Shares mappedGlData's single scan.
   */
  mappedGlOwnerProductLineById = (productLinesById, modifiedProductLinesById) => {
    return this.mappedGlData(productLinesById, modifiedProductLinesById).ownerByGlId;
  };

  /**
   * Shared memo: builds both the mapped-GL Set and the owner lookup in one
   * scan, cached on the (productLinesById, modifiedProductLinesById) references.
   */
  mappedGlData = (productLinesById, modifiedProductLinesById) => {
    if (
      this._mappedGlCache &&
      this._mappedGlCache.productLinesById === productLinesById &&
      this._mappedGlCache.modifiedProductLinesById === modifiedProductLinesById
    ) {
      return this._mappedGlCache.data;
    }

    const data = buildMappedGlData(productLinesById, modifiedProductLinesById, isUnmappedGl);

    this._mappedGlCache = { productLinesById, modifiedProductLinesById, data };
    return data;
  };

  productGroupOptions = (productGroupsById) => {
    let options = [];
    for (let id of Object.keys(productGroupsById)) {
      const productGroup = productGroupsById[id];
      options.push({
        label: `${productGroup ? productGroup.name : 'Unknown'} (${id})`,
        value: id.toString(),
      });
    }

    return options;
  };

  parentSelectionComponent = (productLine, parentProductLineOptions) => {
    const parentId = productLine && productLine.parent;
    const selected = parentProductLineOptions.find((option) => option.value === parentId);
    return (
      <div key={`PARENT_SELECTION_${productLine.id}_${parentId}`}>
        <Select
          options={parentProductLineOptions}
          defaultValue={selected}
          onChange={(selectedOption) => this.onParentProductLineChange(selectedOption, productLine)}
          isMulti={false}
          isDisabled={this.props.readOnly}
        />
      </div>
    );
  };

  tableContentSelectorOptions = (columnDefinitions) => {
    return [
      {
        options: columnDefinitions.map((entry) => {
          return {
            id: entry.id,
            label: entry.header,
            visible: !hiddenColumns.includes(entry.id),
          };
        }),
        label: '',
      },
    ];
  };

  parentProductLineOptions = (productLines) => {
    return productLines.map((productLine) => {
      return {
        value: productLine.id,
        label: `${productLine.name} (${productLine.id})`,
      };
    });
  };

  /**
   * Product lines are grouped by their first level ancestors - i.e. "Physical Consumer"
   */
  groupingAncestorProductLineById = (productLines) => {
    const firstLevelProductLines = productLines.filter((p) => p.parent === 'ALL');
    let parentProductLineToFirstLevelMapping = {};

    for (let productLineFirstLevel of firstLevelProductLines) {
      let childrenIds = productLineFirstLevel.childrenIds ? productLineFirstLevel.childrenIds.slice() : [];
      let childId = childrenIds.shift();
      while (childId) {
        if (isNaN(childId)) {
          const child = productLines.filter((p) => p.id === childId);
          child[0] != null && child[0].childrenIds != null && childrenIds.unshift(...child[0].childrenIds);
          parentProductLineToFirstLevelMapping[childId] = productLineFirstLevel;
        }
        childId = childrenIds.shift();
      }
    }

    let groupingAncestorProductLineById = {};
    for (let leafProductLine of productLines.filter((p) => p.isLeaf)) {
      groupingAncestorProductLineById[leafProductLine.id] =
        parentProductLineToFirstLevelMapping[leafProductLine.parent];
    }

    return groupingAncestorProductLineById;
  };

  getModfiedProductLine = (productLine) => {
    const { id } = productLine;
    return this.state.modifiedProductLinesById[id]
      ? { ...this.state.modifiedProductLinesById[id] }
      : { ...productLine };
  };

  getOriginalProductLine = (productLine) => {
    const { productLinesById } = this.props;
    const { id } = productLine;
    return productLinesById[id];
  };

  onProductGroupSelection = (productLine, selectedProductGroups) => {
    const originalProductLine = this.getOriginalProductLine(productLine);
    const modifiedProductLine = this.getModfiedProductLine(productLine);

    const originalGls = originalProductLine.gls;
    const modfiedGLs = selectedProductGroups.map((item) => parseInt(item.value));
    if (!modfiedGLs.length && isUnmappedGl(originalGls)) {
      // If GL was not modified - we reset it back to the original.
      // Original GL can be 'null', [-1], etc.
      modifiedProductLine.gls = originalGls;
    } else {
      modifiedProductLine.gls = modfiedGLs;
    }

    this.handleProductLineModification(originalProductLine, modifiedProductLine);
  };

  onParentProductLineChange = (selectedOption, productLine) => {
    const { productLinesById } = this.props;
    const originalProductLine = this.getOriginalProductLine(productLine);
    const modifiedProductLine = this.getModfiedProductLine(productLine);

    const selectedParentId = selectedOption.value;
    const selectedParent = productLinesById[selectedParentId];

    modifiedProductLine.parentItem = selectedParent;
    modifiedProductLine.parent = selectedParent.id;

    this.handleProductLineModification(originalProductLine, modifiedProductLine);
  };

  onExcludedChange = (event, productLine) => {
    const originalProductLine = this.getOriginalProductLine(productLine);
    const modifiedProductLine = this.getModfiedProductLine(productLine);
    modifiedProductLine.excluded = Boolean(event.detail.checked);

    this.handleProductLineModification(originalProductLine, modifiedProductLine);
  };

  onIgnoredChange = (event, productLine) => {
    const originalProductLine = this.getOriginalProductLine(productLine);
    const modifiedProductLine = this.getModfiedProductLine(productLine);
    modifiedProductLine.ignored = Boolean(event.detail.checked);

    this.handleProductLineModification(originalProductLine, modifiedProductLine);
  };

  handleProductLineModification = (originalProductLine, modifiedProductLine) => {
    // Newly unmapped PLs get -1 (Unmapped) mapping
    if (!modifiedProductLine.gls || !modifiedProductLine.gls.length) {
      modifiedProductLine.gls = [-1]
    }

    // Newly mapped PLs lose -1 (Unmapped) mapping
    if (modifiedProductLine.gls && modifiedProductLine.gls.length > 1 && modifiedProductLine.gls.includes(-1)) {
      modifiedProductLine.gls = modifiedProductLine.gls.splice(modifiedProductLine.gls.indexOf(-1), 1)
    }

    const { id } = originalProductLine;
    let modifiedProductLinesById = {
      ...this.state.modifiedProductLinesById,
      [id]: modifiedProductLine,
    };

    this.setState({ modifiedProductLinesById }, () => this.getLatestVersion(originalProductLine));
  };

  getLatestVersion = (productLine) => {
    const { refreshPlHierarchy } = this.props;

    refreshPlHierarchy(productLine.id, PRODUCT_LINE_LATEST_VERSION, this.getLatestVersionSuccess);
  };

  getLatestVersionSuccess = (data) => {
    const { productLinesById } = this.props;
    const { modifiedProductLinesById } = this.state;

    const latestProductLine = data.root;
    const { id } = latestProductLine;
    const modifiedProductLine = modifiedProductLinesById[id];

    if (!isProductLineModified(latestProductLine, modifiedProductLine)) {
      this.unstageProductLineChanges([id], { ...productLinesById, [id]: latestProductLine });
    } else {
      this.props.stageProductLineChanges(modifiedProductLinesById);
    }

    this.setState({ modifiedProductLinesById: {} });
  };

  reviewChangesScreenDismiss = () => {
    this.setState({
      reviewChangesScreen: {
        visible: false,
        stagedProductLines: null,
        latestProductLinesById: null,
        productGroupsById: null,
      },
    });
  };

  hasStagedChanges = () => {
    const { productLinesById } = this.props;
    for (let id of Object.keys(productLinesById || {})) {
      if (productLinesById[id].isStaged && productLinesById[id].isLeaf) {
        return true;
      }
    }

    return false;
  };

  getStagedProductLines = () => {
    const { productLinesById } = this.props;
    let stagedProductLines = [];
    for (let id of Object.keys(productLinesById || {})) {
      const productLine = productLinesById[id];
      if (productLine.isStaged && productLine.isLeaf) {
        stagedProductLines.push(productLinesById[id]);
      }
    }

    return stagedProductLines;
  };

  onClickReviewPublishChanges = () => {
    const { refreshPlHierarchy } = this.props;

    refreshPlHierarchy(PRODUCT_LINE_ROOT, PRODUCT_LINE_LATEST_VERSION, this.refreshPlHierarchySuccess);
  };

  onPropertyFilteringChange = (event) => {
    this.setState({
      filteringTokens: event.detail.tokens || [],
      filteringOperation: event.detail.operation || 'and',
    });
  };

  onClickDownloadData = (columnDefinitions, productLinesToDisplay) => {
    const { filteringTokens, filteringOperation } = this.state;

    const filteredProductLines = getFilteredItems(productLinesToDisplay, filteringTokens, filteringOperation, ARRAY_FIELD_BY_PROPERTY_KEY);

    const downloadColumnDefinitions = [
      ...columnDefinitions,
      { id: '_mappingStatus', header: 'Mapping Status' },
    ];

    const data = toDownloadFormat(downloadColumnDefinitions, filteredProductLines);
    downloadFile(data, 'PL_GL_Mapping.xls');
  };

  refreshPlHierarchySuccess = (data) => {
    let productLinesById = {};
    accumulateProductLineById(data.root, null, productLinesById);

    const { productGroupsById } = this.props;
    this.setState({
      reviewChangesScreen: {
        visible: true,
        stagedProductLines: this.getStagedProductLines(),
        latestProductLinesById: productLinesById,
        productGroupsById,
        comment: 'Changes by user: ' + this.props.user + ' at ' + new Date().toUTCString(),
      },
    });
  };

  unstageProductLineChanges = (selectedProductLineIds, latestProductLinesById) => {
    const { unstageProductLineChanges, productGroupsById } = this.props;

    unstageProductLineChanges(selectedProductLineIds, latestProductLinesById);

    this.setState({
      reviewChangesScreen: {
        visible: this.state.reviewChangesScreen.visible,
        stagedProductLines: this.getStagedProductLines(),
        latestProductLinesById,
        productGroupsById,
      },
    });
  };

  publishProductLineChanges = (selectedProductLineIds, latestProductLinesById, stagedProductLines) => {
    const { publishProductLineChanges, productGroupsById } = this.props;

    publishProductLineChanges(selectedProductLineIds, latestProductLinesById, stagedProductLines);

    this.setState({
      reviewChangesScreen: {
        visible: this.state.reviewChangesScreen.visible,
        stagedProductLines: this.getStagedProductLines(),
        latestProductLinesById,
        productGroupsById,
      },
    });
  };

  withAdditionalSearchableFields = (productLine, productGroupsById, parentLabelById) => {
    // Per-product-group display tokens ("name (id)"). Kept as an array so the filter-
    // option builder enumerates each token directly rather than splitting the joined
    // _productGroupsText back apart -- a product-group name containing a comma would
    // otherwise corrupt that round-trip. _productGroupsText stays the joined,
    // substring-searchable display field the property filter matches on.
    const productGroupTokens = productLine.gls
      ? productLine.gls
          .map((gl) => productGroupsById[gl])
          .filter((item) => item)
          .map((productGroup) => `${productGroup.name} (${productGroup.id})`)
      : [];
    return {
      ...productLine,
      // Mapped/unmapped status token for the property filter (mirrors the Unmapped badge).
      _mappingStatus: isUnmappedGl(productLine.gls, productLine.ignored) ? PRODUCT_LINE_STATUS.UNMAPPED : 'Single mapped',
      _stagedText: productLine.isStaged ? PRODUCT_LINE_STATUS.STAGED : '',
      // Must equal the Product Line Group cell text; its dropdown options are full-match tokens.
      _ancestorText: productLine.ancestorProductLineName
        ? `${productLine.ancestorProductLineName} (${productLine.ancestorProductLine})`
        : '',
      // Friendly parent label ("name (id)") so Parent can be filtered by name, not raw id.
      _parentText: (parentLabelById && parentLabelById[productLine.parent]) || productLine.parent || '',
      _productGroupTokens: productGroupTokens,
      _productGroupsText: productGroupTokens.join(),
    };
  };

  /**
   * Memoized wrapper around getFilteringOptions.
   *
   * `productLinesToDisplay` is rebuilt (new array identity) on every render, so
   * it cannot itself be a cache key. Instead we key on the three UPSTREAM data
   * references the display list is derived from: the persisted `productLinesById`
   * prop, the staged `modifiedProductLinesById` state, and `productGroupsById`.
   * These are reference-SWAPPED (not mutated in place) whenever the underlying
   * data actually changes - productLinesById/productGroupsById are rebuilt on
   * load (accumulateProductLineById) and modifiedProductLinesById is replaced via
   * setState spread - so object identity is a trustworthy invalidation signal
   * here, which is why we prefer it over a deep comparison (the codebase mutates
   * some state objects elsewhere, making deep compare unreliable and expensive).
   */
  getFilteringOptionsMemoized = (productLines, productLinesById, modifiedProductLinesById, productGroupsById) => {
    if (
      this._filteringOptionsCache &&
      this._filteringOptionsCache.productLinesById === productLinesById &&
      this._filteringOptionsCache.modifiedProductLinesById === modifiedProductLinesById &&
      this._filteringOptionsCache.productGroupsById === productGroupsById
    ) {
      return this._filteringOptionsCache.filteringOptions;
    }

    const filteringOptions = this.getFilteringOptions(productLines);
    this._filteringOptionsCache = {
      productLinesById,
      modifiedProductLinesById,
      productGroupsById,
      filteringOptions,
    };
    return filteringOptions;
  };

  // Build multi-property filtering options from the real product-line columns.
  // Computed/array columns (parent label, ancestor, mapped product groups) are
  // flattened into synthetic `_<x>Text` / `_mappingStatus` strings on each item; the pickable values
  // below substring-match those fields.
  getFilteringOptions = (productLines) => {
    const idOptions = new Set();
    const nameOptions = new Set();
    const parentOptions = new Set();
    const ancestorOptions = new Set();
    const productGroupOptions = new Set();
    (productLines || []).forEach((productLine) => {
      if (productLine.id != null) idOptions.add(`${productLine.id}`);
      if (productLine.name) nameOptions.add(productLine.name);
      if (productLine._parentText) parentOptions.add(productLine._parentText);
      if (productLine._ancestorText) ancestorOptions.add(productLine._ancestorText);
      if (productLine._productGroupTokens) {
        productLine._productGroupTokens.forEach((group) => group && productGroupOptions.add(group));
      }
    });
    return [
      { groupValuesLabel: 'Id Values', propertyKey: 'id', propertyLabel: 'Id', values: capFilterValues(Array.from(idOptions)) },
      { groupValuesLabel: 'Name Values', propertyKey: 'name', propertyLabel: 'Name', values: capFilterValues(Array.from(nameOptions)) },
      { groupValuesLabel: 'Parent Values', propertyKey: '_parentText', propertyLabel: 'Parent', values: capFilterValues(Array.from(parentOptions)) },
      { groupValuesLabel: 'Product Line Group Values', propertyKey: '_ancestorText', propertyLabel: 'Product Line Group', values: capFilterValues(Array.from(ancestorOptions)) },
      { groupValuesLabel: 'Product Groups Values', propertyKey: '_productGroupsText', propertyLabel: 'Product Groups', values: capFilterValues(Array.from(productGroupOptions)) },
      // Mapped/unmapped and staged status tokens mirror the row badges.
      { groupValuesLabel: 'Mapping status Values', propertyKey: '_mappingStatus', propertyLabel: 'Mapping status', values: ['Single mapped', PRODUCT_LINE_STATUS.UNMAPPED] },
      { groupValuesLabel: 'Staged Values', propertyKey: '_stagedText', propertyLabel: 'Staged', values: [PRODUCT_LINE_STATUS.STAGED] },
    ];
  };

  render() {
    const { productLinesById, productGroupsById, errorScreenMessage } = this.props;
    const { reviewChangesScreen, modifiedProductLinesById, filteringTokens } = this.state;

    let leafProductLines = productLinesById ? Object.values(productLinesById).filter((item) => item.isLeaf) : [];
    let nonLeafProductLines = productLinesById ? Object.values(productLinesById).filter((item) => !item.isLeaf) : [];

    const groupingAncestorProductLineById = this.groupingAncestorProductLineById(Object.values(productLinesById));

    // GL ids already mapped to some (leaf) product line, respecting staged edits.
    // Computed once here (not per row) and reused for every row's exclusion.
    const mappedGlIds = this.mappedGlProductGroupIds(productLinesById, modifiedProductLinesById);
    // Owning PL per mapped GL, for the disabled-option badge.
    const mappedGlOwnerById = this.mappedGlOwnerProductLineById(productLinesById, modifiedProductLinesById);

    // Friendly "name (id)" label per non-leaf parent, so the Parent filter offers names.
    const parentLabelById = {};
    nonLeafProductLines.forEach((productLine) => {
      parentLabelById[productLine.id] = `${productLine.name} (${productLine.id})`;
    });

    const columnDefinitions = productGroupsById
      ? this.getColumnDefinitions(
          this.productGroupOptions(productGroupsById),
          this.parentProductLineOptions(nonLeafProductLines),
          mappedGlIds,
          mappedGlOwnerById
        )
      : [];

    const productLinesToDisplay = leafProductLines
      .filter((p) => !hiddenProductLineIds.includes(p.id))
      .map((p) => (modifiedProductLinesById[p.id] ? modifiedProductLinesById[p.id] : p))
      .map((p) => {
        return {
          ...p,
          ancestorProductLine: groupingAncestorProductLineById[p.id] ? groupingAncestorProductLineById[p.id].id : '',
          ancestorProductLineName: groupingAncestorProductLineById[p.id]
            ? groupingAncestorProductLineById[p.id].name
            : '',
        };
      })
      .map((p) => this.withAdditionalSearchableFields(p, productGroupsById, parentLabelById));

    return (
      <div className="pl-gl-mapping-table">
        <Table
          columnDefinitions={columnDefinitions}
          items={productLinesToDisplay}
          header={
            <div className="awsui-util-action-stripe">
              <div className="awsui-util-action-stripe-title">
                <h2>
                  PL GL Mapping <span className="awsui-util-header-counter">({productLinesToDisplay.length})</span>
                </h2>
              </div>
              <div className="awsui-util-action-stripe-group">
                { !this.props.readOnly && (
                <Button
                    text="Review/Publish Changes"
                    variant="primary"
                    onClick={this.onClickReviewPublishChanges}
                    disabled={!this.hasStagedChanges()}
                />
                )}
                <Button
                  text="Download Data"
                  onClick={() => this.onClickDownloadData(columnDefinitions, productLinesToDisplay)}
                  icon="download"
                />
              </div>
            </div>
          }
          loadingText={'Loading product lines...'}
          loading={!productLinesToDisplay || !productLinesToDisplay.length}
          stickyHeader
        >
          <TablePropertyFiltering
            {...V2_PROPERTY_FILTERING_TEXT}
            filteringOptions={this.getFilteringOptionsMemoized(
              productLinesToDisplay,
              productLinesById,
              modifiedProductLinesById,
              productGroupsById
            )}
            filteringFunction={filterProductLine}
            tokens={filteringTokens}
            operation={this.state.filteringOperation}
            onPropertyFilteringChange={this.onPropertyFilteringChange}
            placeholder="Filter product lines"
          />
          <TableSorting
            sortingColumn="id"
            sortableColumns={[
              { id: 'id', field: 'id' },
              { id: 'name', field: 'name' },
              { id: 'parent', field: 'parent' },
              { id: 'ancestorProductLine', field: 'ancestorProductLine' },
              { id: 'excluded', field: 'excluded' },
              { id: 'ignored', field: 'ignored' },
              { id: 'staged', field: 'staged' },
              { id: 'user', field: 'user' },
              { id: 'time', field: 'time' },
            ]}
          />
          <TablePagination pageSize={tablePageSize} />
          <TablePreferences title="Preferences" confirmLabel="Confirm" cancelLabel="Cancel">
            <TableContentSelector
              title="Select visible columns"
              options={this.tableContentSelectorOptions(columnDefinitions)}
            />
          </TablePreferences>
        </Table>
        <ReviewChanges
          {...reviewChangesScreen}
          unstageProductLineChanges={this.unstageProductLineChanges}
          publishProductLineChanges={this.publishProductLineChanges}
          onDismiss={this.reviewChangesScreenDismiss}
          errorScreenMessage={errorScreenMessage}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/config_central/pl_gl_mapping/pl_gl_mapping_table.jsx